import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../interfaces/api-response.interface';

export interface LoginResponse {
    sessionId?: string;
    trxId?: string;
    authCode?: string;
    selectedRole?: string;
    username?: string;
}

export interface UserProfile {
    employName?: string | null;
    sub?: string;
    emailVerified?: boolean;
    memberNumber?: string | null;
    policyNumber?: string | null;
    username?: string;
    userType?: string | null;
    clientNumber?: string | null;
    subNumber?: string | null;
    selectedRole?: string | null;
}

export interface OTPResponse {
    authCode?: string;
    selectedRole?: string;
    userProfile?: UserProfile;
}

export interface SignupRequest {
    policyNumber: string;
    memberNumber: string;
    mobileNumber: string;
    email: string;
    password: string;
    confirmPassword: string;
    employType: string;
}

export interface SignupResponse {
    message?: string;
    userId?: string;
}

@Injectable({
    providedIn: 'root'
})
export class EmployeeAuthService {
    private readonly employeeAuthPath = environment.EMPLOYEE_AUTH;
    constructor(private http: HttpClient) { }

    private getHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'withCredentials': 'true',
        });
    }

    /**
     * Register new employee
     */
    signup(signupData: SignupRequest): Observable<ApiResponse<SignupResponse>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.signupPath}`;

        return this.http.post<ApiResponse<SignupResponse>>(url, signupData, {
            headers: this.getHeaders()
        });
    }

    /**
     * Validate employee credentials
     */
    validateCredentials(username: string, password: string): Observable<ApiResponse<LoginResponse>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.loginPath}/validate-credentials`;
        const body = {
            username,
            password,
            portalId: 'employee'
        };

        return this.http.post<ApiResponse<LoginResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Submit OTP for verification
     * @param sessionId
     * @param otp
     * @param trxId
     * @param isSignup Set to true when verifying OTP for signup flow so the request is sent to the signup endpoint
     */
    submitOTP(sessionId: string, otp: string, trxId?: string, isSignup: boolean = false): Observable<ApiResponse<OTPResponse>> {
        const path = isSignup ? this.employeeAuthPath.signupPath : this.employeeAuthPath.loginPath;
        const url = `${this.employeeAuthPath.baseUrl}${path}/submit-otp`;
        const body = {
            sessionId,
            otp,
            trxId
        };

        return this.http.post<ApiResponse<OTPResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Resend OTP
     */
    resendOTP(sessionId: string): Observable<ApiResponse> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.loginPath}/resend-otp`;
        const body = {
            sessionId
        };

        return this.http.post<ApiResponse>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Request username recovery for employee
     */
    recoverUsername(policyNumber: string, memberNumber: string): Observable<ApiResponse<LoginResponse>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.forgotPasswordPath}/forgot-username`;
        const body = {
            policyNumber,
            memberNumber
        };

        return this.http.post<ApiResponse<LoginResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Initiate forgot password (check user exist)
     */
    initiateForgotPassword(email: string): Observable<ApiResponse<LoginResponse>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.forgotPasswordPath}/initiate`;
        const body = {
            username: email
        };
        return this.http.post<ApiResponse<LoginResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Submit OTP for forgot password
     */
    verifyForgotPasswordOTP(sessionId: string, otp: string): Observable<ApiResponse<OTPResponse>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.forgotPasswordPath}/verify-otp`;
        const body = {
            sessionId,
            otp
        };
        return this.http.post<ApiResponse<OTPResponse>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Reset password after OTP verification
     */
    resetForgotPassword(sessionId: string, password: string, confirmPassword: string): Observable<ApiResponse<any>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.forgotPasswordPath}/reset-password`;
        const body = {
            sessionId,
            password,
            confirmPassword
        };
        return this.http.post<ApiResponse<any>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Resend OTP for forgot password
     */
    resendForgotPasswordOTP(sessionId: string): Observable<ApiResponse<any>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.forgotPasswordPath}/resend-otp`;
        const body = {
            sessionId
        };
        return this.http.post<ApiResponse<any>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Change password after reset verification
     */
    changePassword(sessionId: string, password: string, confirmPassword: string): Observable<ApiResponse<any>> {
        const url = `${this.employeeAuthPath.baseUrl}${this.employeeAuthPath.forgotPasswordPath}/reset-password`;
        const body = {
            sessionId,
            password,
            confirmPassword
        };

        return this.http.post<ApiResponse<any>>(url, body, {
            headers: this.getHeaders()
        });
    }

    /**
     * Validate employee signup fields (new flow)
     */
    validateSignup(signupData: SignupRequest): Observable<ApiResponse<any>> {
        const url = `${this.employeeAuthPath.baseUrl}/customidp/employee/signup`;
        return this.http.post<ApiResponse<any>>(url, signupData, {
            headers: this.getHeaders()
        });
    }

    /**
     * Accept terms for employee signup
     */
    acceptTerms(payload: { sessionId: string; termsAccepted: boolean; privacyAccepted: boolean; locationAccepted: boolean; contactAccepted: boolean }): Observable<ApiResponse<any>> {
        const url = `${this.employeeAuthPath.baseUrl}/customidp/employee/signup/accept-terms`;
        return this.http.post<ApiResponse<any>>(url, payload, {
            headers: this.getHeaders()
        });
    }
} 